// Maps Bitmovin VIDEO_ID values back to something a human recognizes.
// Live streams report their stream key; VOD files report their refId.

/**
 * Normalize a raw id the way core does (strip .mp4).
 */
export function normalizeContentId(id) {
	if (id === null || id === undefined) return '';
	return String(id).replace(/\.mp4$/i, '');
}

/**
 * Key used for a VOD file in the analytics API.
 */
export function vodFileKey(file) {
	if (!file) return '';
	if (file.refId) return String(file.refId);
	if (file.archive?.key) return String(file.archive.key);
	return normalizeContentId(file.id ?? file._id ?? '');
}

/**
 * Build a lookup from live streams (store) and known VOD files.
 * @param {Array} liveStreams items with { key, name, _id }
 * @param {Array} vodFiles items with { refId|id, fileName, playlistName }
 */
export function buildContentIndex(liveStreams = [], vodFiles = []) {
	const index = new Map();
	for (const s of liveStreams) {
		if (!s?.key) continue;
		index.set(String(s.key), {
			id: String(s.key),
			name: s.name || s.key,
			type: 'live',
			streamId: s._id,
			enabled: s.enabled !== false,
		});
	}
	for (const f of vodFiles) {
		const key = vodFileKey(f);
		if (!key) continue;
		const entry = {
			id: key,
			name: f.fileName || f.name || key,
			type: 'vod',
			folderId: f.playlistId || f.streamId || f.folderId || null,
			folderName: f.playlistName || null,
			durationSec: f.mediaInfo?.duration || null,
		};
		index.set(key, entry);
		// Deep links may carry the file id (foo.mp4) rather than the refId.
		const alias = normalizeContentId(f.id ?? f._id ?? '');
		if (alias && alias !== key && !index.has(alias)) index.set(alias, entry);
	}
	return index;
}

/**
 * Resolve an id to display info; always returns an object so the table can
 * render a raw id instead of a blank cell.
 */
export function resolveContent(index, rawId) {
	const id = normalizeContentId(rawId);
	if (index && index.has(id)) return index.get(id);
	// Live stream keys are `live_<hex>`; every other id the analytics API
	// reports is a VOD refId, so an unresolved one still classifies (and
	// filters by scope) as a video rather than an unknown "content".
	const type = id.startsWith('live_') ? 'live' : 'vod';
	return {
		id,
		// Never surface the bare live_<hex>/refId; callers show a skeleton while
		// names are still loading and this label once nothing matched.
		name: unresolvedLabel(type),
		rawId: id,
		type,
		unresolved: true,
	};
}

/**
 * Human label for an id nothing in the stores could name.
 */
export function unresolvedLabel(type) {
	if (type === 'live') return 'Untitled live stream';
	return 'Untitled video';
}

/**
 * Filter selected ids by scope ('all' | 'live' | 'vod'). Unresolved ids
 * classify by their shape (see resolveContent).
 */
export function filterIdsByScope(index, ids, scope) {
	if (!scope || scope === 'all') return ids.slice();
	return ids.filter((id) => resolveContent(index, id).type === scope);
}
